import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, WifiOff, Wifi } from 'lucide-react';

export const VideoCallDebugPanel = ({ 
  socket, 
  localStream, 
  remoteStreams, 
  peerConnections,
  isVisible = false 
}) => {
  const [connectionStats, setConnectionStats] = useState({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(async () => {
      const stats = {};

      // Get stats for each peer connection
      for (const [userId, pc] of Object.entries(peerConnections.current || {})) {
        try {
          const pcStats = await pc.getStats();
          const iceState = pc.iceConnectionState;
          const connectionState = pc.connectionState;
          
          let videoStats = null;
          let audioStats = null;

          pcStats.forEach(report => {
            if (report.type === 'inbound-rtp') {
              if (report.kind === 'video') {
                videoStats = {
                  bytesReceived: report.bytesReceived,
                  packetsLost: report.packetsLost,
                  framesDecoded: report.framesDecoded,
                  frameHeight: report.frameHeight,
                  frameWidth: report.frameWidth
                };
              } else if (report.kind === 'audio') {
                audioStats = {
                  bytesReceived: report.bytesReceived,
                  packetsLost: report.packetsLost,
                  audioLevel: report.audioLevel
                };
              }
            }
          });

          stats[userId] = {
            iceState,
            connectionState,
            videoStats,
            audioStats
          };
        } catch (error) {
          console.error('Error getting stats:', error);
        }
      }

      setConnectionStats(stats);
    }, 2000);

    return () => clearInterval(interval);
  }, [peerConnections, isVisible]);

  if (!isVisible) return null;

  const getStatusIcon = (state) => {
    if (state === 'connected' || state === 'completed') {
      return <CheckCircle size={16} style={{ color: '#10b981' }} />;
    } else if (state === 'failed' || state === 'disconnected') {
      return <XCircle size={16} style={{ color: '#ef4444' }} />;
    } else {
      return <AlertCircle size={16} style={{ color: '#f59e0b' }} />;
    }
  };

  const getStatusColor = (state) => {
    if (state === 'connected' || state === 'completed') return '#10b981';
    if (state === 'failed' || state === 'disconnected') return '#ef4444';
    return '#f59e0b';
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: expanded ? '400px' : '60px',
      maxHeight: expanded ? '500px' : '60px',
      background: 'rgba(0, 0, 0, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
      transition: 'all 0.3s ease',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div 
        style={{
          padding: '12px',
          borderBottom: expanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <>
            <span style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
              Debug Info
            </span>
            <Wifi size={20} style={{ color: '#10b981' }} />
          </>
        ) : (
          <div style={{ 
            width: '36px', 
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto'
          }}>
            <Wifi size={24} style={{ color: '#10b981' }} />
          </div>
        )}
      </div>

      {/* Content */}
      {expanded && (
        <div style={{
          padding: '12px',
          maxHeight: '400px',
          overflowY: 'auto',
          fontSize: '12px',
          color: '#e5e5e5'
        }}>
          {/* Socket Status */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              color: '#888', 
              fontSize: '11px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Socket Connection
            </h4>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '8px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '6px'
            }}>
              {socket?.connected ? (
                <CheckCircle size={14} style={{ color: '#10b981' }} />
              ) : (
                <WifiOff size={14} style={{ color: '#ef4444' }} />
              )}
              <span>
                {socket?.connected ? 'Connected' : 'Disconnected'}
              </span>
              {socket?.id && (
                <span style={{ 
                  marginLeft: 'auto', 
                  fontSize: '10px', 
                  color: '#888' 
                }}>
                  {socket.id.slice(-6)}
                </span>
              )}
            </div>
          </div>

          {/* Local Stream */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              color: '#888', 
              fontSize: '11px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Local Stream
            </h4>
            <div style={{
              padding: '8px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '6px'
            }}>
              {localStream ? (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <CheckCircle size={14} style={{ color: '#10b981' }} />
                    <span>Active</span>
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#888',
                    marginLeft: '22px'
                  }}>
                    Video: {localStream.getVideoTracks().length} track(s)<br />
                    Audio: {localStream.getAudioTracks().length} track(s)
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <XCircle size={14} style={{ color: '#ef4444' }} />
                  <span>No stream</span>
                </div>
              )}
            </div>
          </div>

          {/* Remote Streams */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              color: '#888', 
              fontSize: '11px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Remote Streams ({Object.keys(remoteStreams || {}).length})
            </h4>
            {Object.keys(remoteStreams || {}).length === 0 ? (
              <div style={{
                padding: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                color: '#888'
              }}>
                No remote streams
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(remoteStreams).map(([userId, stream]) => (
                  <div 
                    key={userId}
                    style={{
                      padding: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <CheckCircle size={14} style={{ color: '#10b981' }} />
                      <span>User {userId.slice(-6)}</span>
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#888',
                      marginLeft: '22px'
                    }}>
                      Video: {stream.getVideoTracks().length}<br />
                      Audio: {stream.getAudioTracks().length}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Peer Connections */}
          <div>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              color: '#888', 
              fontSize: '11px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Peer Connections
            </h4>
            {Object.keys(connectionStats).length === 0 ? (
              <div style={{
                padding: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                color: '#888'
              }}>
                No peer connections
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(connectionStats).map(([userId, stats]) => (
                  <div 
                    key={userId}
                    style={{
                      padding: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '6px'
                    }}>
                      {getStatusIcon(stats.connectionState)}
                      <span>User {userId.slice(-6)}</span>
                    </div>
                    
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#888',
                      marginLeft: '22px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}>
                      <div>
                        <span style={{ color: '#666' }}>ICE:</span>{' '}
                        <span style={{ color: getStatusColor(stats.iceState) }}>
                          {stats.iceState}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#666' }}>Connection:</span>{' '}
                        <span style={{ color: getStatusColor(stats.connectionState) }}>
                          {stats.connectionState}
                        </span>
                      </div>
                      
                      {stats.videoStats && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ color: '#666' }}>Video:</span>{' '}
                          {stats.videoStats.frameWidth}x{stats.videoStats.frameHeight},{' '}
                          {stats.videoStats.packetsLost} lost
                        </div>
                      )}
                      
                      {stats.audioStats && (
                        <div>
                          <span style={{ color: '#666' }}>Audio:</span>{' '}
                          Level {(stats.audioStats.audioLevel * 100).toFixed(0)}%,{' '}
                          {stats.audioStats.packetsLost} lost
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
